"use client"

import * as React from "react"
import { Moon, Sun } from "lucide-react"
import { useTheme } from "next-themes"
import { Button } from "@/components/ui/button"
import { useTranslations } from "next-intl"

/**
 * ThemeToggle Component
 * Simple toggle between light and dark themes
 * Uses next-themes for theme management
 *
 * Features:
 * - Single click toggles between light and dark
 * - Sun/Moon icons based on current theme
 * - Accessible with keyboard navigation
 * - Touch-friendly 44x44px minimum target
 */

export function ThemeToggle() {
  const { setTheme, theme } = useTheme()
  const t = useTranslations('common.theme')
  const [mounted, setMounted] = React.useState(false)

  // Avoid hydration mismatch
  React.useEffect(() => {
    setMounted(true)
  }, [])

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark")
  }

  if (!mounted) {
    return (
      <Button
        variant="ghost"
        size="icon"
        className="h-11 w-11"
        disabled
        aria-label="Toggle theme"
      >
        <Sun className="h-5 w-5" />
      </Button>
    )
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-11 w-11"
      onClick={toggleTheme}
      aria-label={t('toggle')}
    >
      <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
      <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
      <span className="sr-only">{t('toggle')}</span>
    </Button>
  )
}
